import React from 'react';
import { useIntl } from 'react-intl';
import { OrganizerData } from './OrganizerData';
import styles from './OrganizerCard.module.scss';
import type { Organizer } from '@/types';
import { ActionBar } from './ActionBar';
import { LISTING_HEAP_PAGE_AREA_IDS } from '@/components/_shared/constants';
import type { OrganizerWithFollowStatus } from '@/components/OrganizerCard/types';
import { OrganizerAvatar } from './OrganizerData/OrganizerAvatar/OrganizerAvatar';
import { ContactButton } from '../_shared/buttons/ContactButton/ContactButton';
import { DEFAULT_FOLLOW_STATUS_RESPONSE } from '../_shared/buttons/FollowButton/utils';
import { useOrganizerFollowStatus } from '../_shared/contexts/FollowOrganizerContext';

export const OrganizerCard: React.FC<{
  organizer: Organizer | null | undefined;
  eventId: string;
  children?: React.ReactNode;
}> = ({ organizer, eventId, children }) => {
  const { formatMessage } = useIntl();
  const { isFollowing, numFollowers, loading } = useOrganizerFollowStatus();
  if (!organizer || !organizer.id) {
    return null;
  }

  if (!organizer.name) {
    return (
      <UnnamedOrganizerCard organizer={organizer} eventId={eventId}>
        {children}
      </UnnamedOrganizerCard>
    );
  }

  const organizerWithFollowStatus: OrganizerWithFollowStatus = {
    ...organizer,
    followStatus: {
      numFollowers: numFollowers ?? DEFAULT_FOLLOW_STATUS_RESPONSE.numFollowers,
      followedByYou: isFollowing ?? DEFAULT_FOLLOW_STATUS_RESPONSE.followedByYou,
    },
    followStatusLoading: loading,
  };

  return (
    <div data-testid="organizer-card" id="organizer-card">
      <div className={styles.title}>{formatMessage({ id: 'Organized by' })}</div>
      <div className={styles.content}>
        <OrganizerData organizer={organizerWithFollowStatus} />
        <ActionBar
          organizer={organizerWithFollowStatus}
          eventId={eventId}
          pageArea={LISTING_HEAP_PAGE_AREA_IDS.NEW_ORGANIZER_PANEL}
        />
      </div>
      {children ? <div className={styles.additionalContent}>{children}</div> : null}
    </div>
  );
};

const UnnamedOrganizerCard: React.FC<{ organizer: Organizer; eventId: string; children?: React.ReactNode }> = ({
  organizer,
  eventId,
  children,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div data-testid="organizer-card">
      <div className={styles.title}>{formatMessage({ id: 'Organized by' })}</div>
      <div className={styles.unnamedContent}>
        <div className={styles.avatarContainer}>
          <OrganizerAvatar organizer={organizer} />
        </div>
        <div className={styles.actionBar}>
          <ContactButton organizerId={organizer.id} eventId={eventId} />
        </div>
      </div>
      {children ? <div className={styles.additionalContent}>{children}</div> : null}
    </div>
  );
};
